import React from 'react';

/**
 * Material UI
 */
import { makeStyles } from '@material-ui/core/styles';
import { Container, Grid, Box, Typography, IconButton, Paper, Divider } from '@material-ui/core';
import { Print as PrintIcon } from '@material-ui/icons';

export const TermsAndConditions = React.memo(function TermsAndConditions(props) {
    const { t } = props;
    const classes = makeStyles(theme => ({
        root: {
            backgroundColor: '#fafafa',
            minHeight: '100vh',
            padding: theme.spacing(6, 0)
        },
        container: {
            maxWidth: '900px',
            margin: '0 auto'
        },
        header: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: theme.spacing(5),
            marginBottom: theme.spacing(5),
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            border: '1px solid #f3f4f6',
            position: 'relative'
        },
        title: {
            fontSize: '2.75rem',
            fontWeight: 800,
            color: '#111827',
            textAlign: 'center',
            marginBottom: theme.spacing(3),
            position: 'relative',
            letterSpacing: '-0.025em',
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '3px',
                backgroundColor: '#ea580c',
                borderRadius: '1.5px'
            }
        },
        subtitle: {
            fontSize: '1.125rem',
            color: '#6b7280',
            textAlign: 'center',
            lineHeight: 1.6,
            fontWeight: 400,
            maxWidth: '600px',
            margin: '0 auto'
        },
        printButton: {
            position: 'absolute',
            top: theme.spacing(3),
            right: theme.spacing(3),
            backgroundColor: 'white',
            color: '#6b7280',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
                backgroundColor: '#f9fafb',
                color: '#ea580c',
                borderColor: '#ea580c'
            }
        },
        content: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: theme.spacing(5),
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            border: '1px solid #f3f4f6'
        },
        section: {
            marginBottom: theme.spacing(5),
            '&:last-child': {
                marginBottom: 0
            }
        },
        sectionTitle: {
            fontSize: '1.375rem',
            fontWeight: 700,
            color: '#111827',
            marginBottom: theme.spacing(3),
            display: 'flex',
            alignItems: 'center',
            letterSpacing: '-0.025em',
            '&::before': {
                content: '""',
                width: '3px',
                height: '28px',
                backgroundColor: '#ea580c',
                borderRadius: '1.5px',
                marginRight: theme.spacing(2.5),
                flexShrink: 0
            }
        },
        sectionText: {
            fontSize: '1rem',
            color: '#374151',
            lineHeight: 1.75,
            marginBottom: theme.spacing(2.5),
            textAlign: 'justify',
            fontWeight: 400,
            letterSpacing: '0.025em'
        },
        link: {
            color: '#ea580c',
            textDecoration: 'none',
            fontWeight: 600,
            borderBottom: '1px solid transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderBottomColor: '#ea580c'
            }
        },
        divider: {
            margin: theme.spacing(4, 0),
            backgroundColor: '#f3f4f6',
            height: '1px'
        },
        lastUpdated: {
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '0.875rem',
            marginTop: theme.spacing(5),
            paddingTop: theme.spacing(3),
            borderTop: '1px solid #f3f4f6',
            fontWeight: 500,
            letterSpacing: '0.025em'
        },
        introduction: {
            backgroundColor: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            padding: theme.spacing(3),
            marginBottom: theme.spacing(4)
        },
        introductionText: {
            color: '#92400e',
            fontSize: '1rem',
            lineHeight: 1.6,
            fontWeight: 500,
            margin: 0
        }
    }))();

    return (
        <Box className={classes.root}>
            <Container className={classes.container}>
                {/* Header Section */}
                <Paper className={classes.header} elevation={0}>
                    <IconButton 
                        className={classes.printButton}
                        onClick={() => window.print()}
                        aria-label="Print"
                    >
                        <PrintIcon />
                    </IconButton>
                    
                    <Typography className={classes.title}>
                        {t("Terms and Conditions")}
                    </Typography>
                    
                    <Typography className={classes.subtitle}>
                        Please read these terms and conditions carefully before using our service. By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.
                    </Typography>
                </Paper>

                {/* Content Section */}
                <Paper className={classes.content} elevation={0}>
                    {/* Introduction */}
                    <Box className={classes.introduction}>
                        <Typography className={classes.introductionText}>
                            Please read these terms and conditions ("terms", "terms and conditions") carefully before using the "{t(process.env.REACT_APP_APPLICATION_NAME)}" website (the "service") operated by "{t(process.env.REACT_APP_COMPANY_NAME)}" ("us", "we", "our").
                        </Typography>
                    </Box>

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Conditions of Use
                        </Typography>
                        <Typography className={classes.sectionText}>
                            We will provide our services to you, which are subject to the conditions stated below in this document. Every time you visit this website, use its services or make a purchase, you accept the following conditions. This is why we urge you to read them carefully.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Privacy Policy
                        </Typography>
                        <Typography className={classes.sectionText}>
                            Before you continue using our website we advise you to read our <a href="/privacy-policies" className={classes.link}>privacy policy</a> regarding our user data collection. It will help you better understand our practices and how we handle your personal information.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Copyright
                        </Typography>
                        <Typography className={classes.sectionText}>
                            Content published on this website (digital downloads, images, texts, graphics, logos) is the property of "{t(process.env.REACT_APP_APPLICATION_NAME)}" and/or its content creators and protected by international copyright laws. The entire compilation of the content found on this website is the exclusive property of "{t(process.env.REACT_APP_COMPANY_NAME)}", with copyright authorship for this compilation by "{t(process.env.REACT_APP_COMPANY_NAME)}".
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Communications
                        </Typography>
                        <Typography className={classes.sectionText}>
                            The entire communication with us is electronic. Every time you send us an email or visit our website, you are going to be communicating with us. You hereby consent to receive communications from us. If you subscribe to the news on our website, you are going to receive regular emails from us. We will continue to communicate with you by posting news and notices on our website and by sending you emails. You also agree that all notices, disclosures, agreements and other communications we provide to you electronically meet the legal requirements that such communications be in writing.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Applicable Law
                        </Typography>
                        <Typography className={classes.sectionText}>
                            By visiting this website, you agree that the laws of the Hong Kong jurisdiction, without regard to principles of conflict laws, will govern these terms and conditions, or any dispute of any sort that might come between "{t(process.env.REACT_APP_COMPANY_NAME)}" and you, or its business partners and associates.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Disputes
                        </Typography>
                        <Typography className={classes.sectionText}>
                            Any dispute related in any way to your visit to this website or to products you purchase from us shall be arbitrated by the court of Hong Kong and you consent to exclusive jurisdiction and venue of such courts.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Comments, Reviews, and Emails
                        </Typography>
                        <Typography className={classes.sectionText}>
                            Visitors may post content as long as it is not obscene, illegal, defamatory, threatening, infringing of intellectual property rights, invasive of privacy or injurious in any other way to third parties. Content has to be free of software viruses, political campaigns, and commercial solicitation.
                        </Typography>
                        <Typography className={classes.sectionText}>
                            We reserve all rights (but not the obligation) to remove and/or edit such content. When you post your content, you grant "{t(process.env.REACT_APP_APPLICATION_NAME)}" non-exclusive, royalty-free and irrevocable right to use, reproduce, publish, modify such content throughout the world in any media.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            License and Site Access
                        </Typography>
                        <Typography className={classes.sectionText}>
                            We grant you a limited license to access and make personal use of this website. You are not allowed to download or modify it. This may be done only with written consent from us.
                        </Typography>
                    </Box>

                    <Divider className={classes.divider} />

                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            User Account
                        </Typography>
                        <Typography className={classes.sectionText}>
                            If you are an owner of an account on this website, you are solely responsible for maintaining the confidentiality of your private user details (username and password). You are responsible for all activities that occur under your account or password.
                        </Typography>
                        <Typography className={classes.sectionText}>
                            We reserve all rights to terminate accounts, edit or remove content and cancel orders in their sole discretion.
                        </Typography>
                    </Box>

                    <Typography className={classes.lastUpdated}>
                        Last updated: {new Date().toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
});